/**
 * CockpitStammdatenAndDocs — Berater-Sicht: Stammdaten-Status + Dokumenten-
 * Übersicht für einen Case.
 *
 * Wird im Cockpit unter dem MandantenStatusBoard angezeigt.
 *
 * Inhalt:
 *   1. Stammdaten-Status („Erfasst" / „Noch unvollständig" / „Nicht erfasst").
 *      Klickbare Disclosure mit Read-Only-Anzeige aller Stammdaten-Felder.
 *   2. Liste der hochgeladenen Dokumente (alle 5 Doc-Typen + sonstige).
 *      Pro Eintrag Status + Download-Link (signed-URL on-click, nicht
 *      gecached).
 *
 * Read-only — der Berater kann hier NICHT direkt editieren. Stammdaten
 * pflegt er über den /case/:id/wizard/stammdaten-Pfad (gleicher Step,
 * andere Audience), Dokumente lädt er über die Mandanten-Box hoch (Berater
 * kann auch hochladen, weil RLS in 0016 das erlaubt).
 */

import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Download, FileX } from 'lucide-react';
import { de } from '../../lib/i18n/de';
import { getCase } from '../../lib/supabaseCases';
import {
  DOCUMENT_TYPES,
  getDocumentDownloadUrl,
  listCaseDocuments,
  type CaseDocument,
  type DocumentType,
} from '../../lib/supabaseDocuments';
import {
  caseToDraft,
  validateStammdaten,
  type StammdatenDraft,
} from '../../lib/stammdaten/stammdatenValidation';
import { DEMO_CASE_ROW, DEMO_DOCUMENTS, isDemoCaseId } from '../../lib/demo/demoFixtures';
import type { CaseRow } from '../../lib/supabaseCases';
import { LoadingRows } from '../ui/Skeleton';

interface CockpitStammdatenAndDocsProps {
  readonly caseId: string;
}

type LoadState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      caseRow: CaseRow;
      docs: ReadonlyArray<CaseDocument>;
    }
  | { kind: 'error'; message: string };

const DOC_ORDER: ReadonlyArray<DocumentType> = [
  'bwa',
  'eks',
  'mietvertrag',
  'steuernummer_nachweis',
  'sonstiges',
];

export function CockpitStammdatenAndDocs({ caseId }: CockpitStammdatenAndDocsProps) {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [showStammdaten, setShowStammdaten] = useState(false);

  const refresh = useCallback(async () => {
    setLoad({ kind: 'loading' });
    // Phase B: Demo-Case → Mock-Fixtures, kein DB-Roundtrip.
    if (isDemoCaseId(caseId)) {
      setLoad({ kind: 'ready', caseRow: DEMO_CASE_ROW, docs: DEMO_DOCUMENTS });
      return;
    }
    try {
      const [c, docs] = await Promise.all([
        getCase(caseId),
        listCaseDocuments(caseId, { onlyActive: true }),
      ]);
      if (!c) {
        setLoad({ kind: 'error', message: 'Mandat nicht gefunden.' });
        return;
      }
      setLoad({ kind: 'ready', caseRow: c, docs });
    } catch (err) {
      setLoad({
        kind: 'error',
        message: err instanceof Error ? err.message : de.mandantBoard.errorLoadFailed,
      });
    }
  }, [caseId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (load.kind === 'loading') {
    return (
      <section className="mb-10 rounded-sm border border-stone-200 bg-white px-6 py-5 md:px-10">
        <LoadingRows rows={2} label="Stammdaten und Dokumente werden geladen" />
      </section>
    );
  }

  if (load.kind === 'error') {
    return (
      <section className="mb-10 rounded-sm border border-red-200 bg-red-50 px-6 py-5 md:px-10">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-red-800">
          Fehler
        </p>
        <p className="mt-1 font-serif text-sm text-red-900">{load.message}</p>
      </section>
    );
  }

  const draft = caseToDraft(load.caseRow);
  const validation = validateStammdaten(draft);
  const stammdatenStatus = validation.isComplete
    ? 'complete'
    : (load.caseRow.client_full_name ?? '').length > 0 ||
        (load.caseRow.business_branch ?? '').length > 0
      ? 'in-progress'
      : 'empty';

  return (
    <section className="mb-10 rounded-sm border border-stone-200 bg-white">
      <div className="border-b border-stone-200 px-6 py-5 md:px-10">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.mandantBoard.sectionLabel}
        </p>
        <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
          STAMMDATEN & DOKUMENTE.
        </h2>
        <p className="mt-2 max-w-prose font-serif text-sm italic text-stone-500">
          Aktueller Stand der vom Mandanten erfassten Stammdaten und hochgeladenen Unterlagen.
        </p>
      </div>

      {/* Stammdaten-Block */}
      <div className="border-b border-stone-200 px-6 py-5 md:px-10">
        <button
          type="button"
          onClick={() => {
            setShowStammdaten((v) => !v);
          }}
          aria-expanded={showStammdaten}
          className="flex w-full items-center justify-between gap-3 text-left"
        >
          <div className="flex items-center gap-3">
            {showStammdaten ? (
              <ChevronDown size={16} className="text-stone-500" />
            ) : (
              <ChevronRight size={16} className="text-stone-500" />
            )}
            <div>
              <p className="font-sans text-[11px] font-medium uppercase tracking-widest text-stone-500">
                {de.mandantBoard.stammdatenLabel}
              </p>
              <p className="mt-0.5 font-serif text-sm font-bold text-stone-900">
                {stammdatenStatus === 'complete'
                  ? de.mandantBoard.stammdatenComplete
                  : stammdatenStatus === 'in-progress'
                    ? de.mandantBoard.stammdatenIncomplete
                    : de.mandantBoard.stammdatenEmpty}
              </p>
            </div>
          </div>
          <span className="font-sans text-[11px] font-medium uppercase tracking-widest text-stone-500 underline-offset-2 hover:underline">
            {showStammdaten
              ? de.mandantBoard.stammdatenHideDetails
              : de.mandantBoard.stammdatenShowDetails}
          </span>
        </button>

        {showStammdaten && <StammdatenReadOnly draft={draft} />}
      </div>

      {/* Dokumenten-Block */}
      <div className="px-6 py-5 md:px-10">
        <p className="font-sans text-[11px] font-medium uppercase tracking-widest text-stone-500">
          {de.mandantBoard.docsLabel}
        </p>
        <ul className="mt-3 divide-y divide-stone-100">
          {DOC_ORDER.map((docType) => {
            const meta = DOCUMENT_TYPES[docType];
            if (!meta) return null;
            const docsOfType = load.docs.filter((d) => d.doc_type === docType);
            return (
              <li key={docType} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-sans text-[11px] font-medium uppercase tracking-widest text-stone-500">
                      {meta.label}
                    </p>
                    {docsOfType.length === 0 ? (
                      <p className="mt-0.5 inline-flex items-center gap-1 font-serif text-sm italic text-stone-500">
                        <FileX size={14} className="text-stone-300" />
                        {de.mandantBoard.docNotUploaded}
                      </p>
                    ) : (
                      <ul className="mt-1 space-y-1">
                        {docsOfType.map((doc) => (
                          <li key={doc.id} className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-serif text-sm text-stone-900">
                                {doc.original_filename}
                              </p>
                              <p className="mt-0.5 font-sans text-[10px] uppercase tracking-widest text-stone-500">
                                {de.mandantBoard.docUploadedAt.replace(
                                  '{date}',
                                  new Date(doc.uploaded_at).toLocaleDateString('de-DE'),
                                )}
                              </p>
                            </div>
                            <DocumentDownloadButton documentId={doc.id} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Read-Only-Anzeige der Stammdaten
// -----------------------------------------------------------------------------

function StammdatenReadOnly({ draft }: { draft: StammdatenDraft }) {
  const rows: ReadonlyArray<{ label: string; value: string }> = [
    { label: 'Name', value: draft.clientFullName },
    { label: 'Geburtsdatum', value: formatDate(draft.clientBirthdate) },
    { label: 'Anschrift', value: draft.clientAddress },
    { label: 'Branche', value: draft.businessBranch },
    { label: 'Rechtsform', value: draft.businessLegalForm },
    { label: 'Beginn der Selbständigkeit', value: formatDate(draft.businessStartDate) },
    { label: 'Steuernummer', value: draft.businessTaxId },
    { label: 'Kurzbeschreibung', value: draft.businessDescription },
  ];

  return (
    <dl className="mt-4 space-y-2">
      {rows.map((r) => (
        <div key={r.label} className="flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-4">
          <dt className="font-sans text-[11px] font-medium uppercase tracking-widest text-stone-500 md:w-48 md:flex-shrink-0">
            {r.label}
          </dt>
          <dd className="whitespace-pre-line font-serif text-sm text-stone-900">
            {r.value || <span className="italic text-stone-500">— noch nicht erfasst</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function formatDate(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString('de-DE');
  } catch {
    return iso;
  }
}

function DocumentDownloadButton({ documentId }: { documentId: string }) {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    setWorking(true);
    setError(null);
    try {
      const url = await getDocumentDownloadUrl(documentId, 300);
      if (typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : de.mandantBoard.docDownloadError);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="flex flex-col items-end">
      <button
        type="button"
        disabled={working}
        onClick={() => {
          void handle();
        }}
        className="inline-flex h-9 items-center gap-1 rounded-sm border border-stone-200 bg-white px-2 font-sans text-[11px] font-medium uppercase tracking-widest text-stone-700 hover:border-stone-900 hover:text-stone-900 disabled:opacity-40"
      >
        <Download size={12} />
        <span className="hidden sm:inline">{de.mandantBoard.docDownload}</span>
      </button>
      {error && <p className="mt-1 font-serif text-xs text-rose-700">{error}</p>}
    </div>
  );
}
